import React, { FC, useRef, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import { IModalLayout } from "Interfaces/interface.modelLayout";
import {
  disableScrollEvents,
  enableScrollEvents,
} from "../../utils/lockScroll";

const ModalLayout: FC<IModalLayout> = ({
  isOpen,
  onClose,
  children,
  title,
  disableCloseButton = false,
  closeOnOutsideClick = true,
}) => {
  const overlayRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen) {
      disableScrollEvents();
    } else {
      enableScrollEvents();
    }

    return () => {
      enableScrollEvents();
    };
  }, [isOpen]);

  const handleClickOutside = useCallback(
    (e: MouseEvent) => {
      if (
        closeOnOutsideClick &&
        overlayRef.current &&
        e.target === overlayRef.current
      ) {
        onClose();
      }
    },
    [closeOnOutsideClick, onClose]
  );

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [closeOnOutsideClick, handleClickOutside]);

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          ref={overlayRef}
          className="fixed inset-0 p-4 z-[100] flex items-center justify-center bg-black/40 backdrop-blur-sm"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <div className="bg-white dark:bg-neutral-800 rounded-2xl w-full max-w-md relative overflow-hidden ">
            <motion.div
              layout
              className="p-6 max-h-[90vh] overflow-y-auto overflow-x-hidden"
              initial={{ scale: 0.95, y: 50, opacity: 0 }}
              animate={{ scale: 1, y: 0, opacity: 1 }}
              exit={{ scale: 0.95, y: 50, opacity: 0 }}
              transition={{ duration: 0.3, ease: "easeOut" as const }}
            >
              <button
                onClick={!disableCloseButton ? onClose : undefined}
                disabled={disableCloseButton}
                className="absolute top-3 right-4 text-neutral-500 hover:text-neutral-800 dark:hover:text-neutral-300"
              >
                <X className="w-5 h-5" />
              </button>

              {title && (
                <h2 className="text-2xl font-semibold text-neutral-600 dark:text-neutral-100 mb-4 text-center">
                  {title}
                </h2>
              )}

              {children}
            </motion.div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ModalLayout;
